import React, { useState } from 'react';
import {
  useGetNewsQuery,
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useDeleteNewsMutation,
  News,
} from '@/store/api/newsApi';
import { useUploadFileMutation } from '@/store/api/storageApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Search,
  Loader2,
  AlertCircle,
  Newspaper,
} from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { Loader } from '@/components/common/Loader';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotifications } from '@/hooks/useNotifications';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function NewsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'Published' | 'Draft' | ''>('');
  const [take, setTake] = useState(10);
  const skip = (page - 1) * take;

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useGetNewsQuery({
    search: debouncedSearch || undefined,
    skip,
    take,
    status: statusFilter || undefined,
  });

  const [createNews] = useCreateNewsMutation();
  const [updateNews] = useUpdateNewsMutation();
  const [deleteNews, { isLoading: isDeleting }] = useDeleteNewsMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [currentNews, setCurrentNews] = useState<Partial<News>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [newsToDelete, setNewsToDelete] = useState<{ id: number; title: string } | null>(null);
  const { success: notifySuccess, error: notifyError } = useNotifications();

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, take]);

  const closeModal = () => {
    setIsEditing(false);
    setCurrentNews({});
    setFormError(null);
  };

  const handleSave = async () => {
    if (!currentNews.title?.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!currentNews.description?.trim()) {
      setFormError('Description is required.');
      return;
    }
    setFormError(null);
    setIsSaving(true);

    const title = currentNews.title.trim();
    const description = currentNews.description.trim();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const date = (
      currentNews.date ? currentNews.date : new Date().toISOString().split('T')[0]
    ) as string;
    const status: 'Draft' | 'Published' = (currentNews.status as 'Draft' | 'Published') ?? 'Draft';

    try {
      if (currentNews.id) {
        await updateNews({
          id: currentNews.id,
          body: { title, description, date, status },
        }).unwrap();
      } else {
        await createNews({ title, description, date, status }).unwrap();
      }
      closeModal();
      notifySuccess('News saved successfully.');
    } catch (err: any) {
      const msg = err?.data?.message;
      setFormError(
        Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!newsToDelete) return;
    try {
      await deleteNews(newsToDelete.id).unwrap();
      notifySuccess('News item deleted.');
      setNewsToDelete(null);
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to delete news. Please try again.');
    }
  };

  const newsItems = response?.data || [];
  const totalPages = response ? Math.ceil(response.count / take) : 0;

  if (isLoading && !newsItems.length) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBanner
        type="error"
        title="Failed to load news"
        message="There was an error loading the news items. Please try again."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">News</h1>
          <p className="text-muted-foreground">
            Manage announcements and news articles for the portal.
          </p>
        </div>
        <Button
          onClick={() => {
            setIsEditing(true);
            setCurrentNews({ status: 'Draft', date: new Date().toISOString().split('T')[0] });
            setFormError(null);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add News
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-4">
          <div>
            <CardTitle>All News</CardTitle>
            <CardDescription>{response?.count ?? 0} news items total</CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-2 mt-4 sm:mt-0 w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'Published' | 'Draft' | '')}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">All Statuses</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
            <select
              value={take}
              onChange={(e) => setTake(Number(e.target.value))}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search title, description..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {isFetching && (
              <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {newsItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-3 font-medium">Title</th>
                      <th className="px-4 py-3 font-medium hidden md:table-cell">Date</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {newsItems.map((news) => (
                      <tr key={news.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4 font-medium text-foreground max-w-xs">
                          <div className="truncate" title={news.title}>
                            {news.title}
                          </div>
                          <div className="text-xs text-muted-foreground font-normal line-clamp-1 mt-0.5">
                            {news.description}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-muted-foreground hidden md:table-cell whitespace-nowrap">
                          {news.date ? news.date.split('T')[0] : '—'}
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant={news.status === 'Published' ? 'default' : 'warning'}>
                            {news.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setIsEditing(true);
                                setCurrentNews({ ...news });
                                setFormError(null);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setNewsToDelete({ id: news.id, title: news.title })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <div className="flex justify-center mb-4">
                  <Newspaper className="h-10 w-10 text-muted-foreground/30" />
                </div>
                <p className="font-medium mb-2">No news items found.</p>
                <p className="text-sm">
                  {searchTerm || statusFilter
                    ? 'Try adjusting your search or filter.'
                    : 'Create your first news announcement to get started.'}
                </p>
              </div>
            )}
          </div>

          {response && response.count > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{skip + 1}</span> to{' '}
                <span className="font-medium">{Math.min(skip + take, response.count)}</span> of{' '}
                <span className="font-medium">{response.count}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={isEditing}
        onClose={closeModal}
        title={currentNews.id ? 'Edit News' : 'Add News'}
      >
        <div className="space-y-4">
          {formError && (
            <div className="flex items-start gap-2 bg-red-50 text-red-700 p-3 rounded-md text-sm border border-red-200">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{formError}</span>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g. Centre publishes new whitepaper"
              value={currentNews.title || ''}
              onChange={(e) => setCurrentNews({ ...currentNews, title: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              placeholder="Brief summary of the news..."
              value={currentNews.description || ''}
              onChange={(e) => setCurrentNews({ ...currentNews, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 flex flex-col">
              <label className="text-sm font-medium">Date</label>
              <DatePicker
                selected={currentNews.date ? new Date(currentNews.date) : null}
                onChange={(date: Date | null) => {
                  if (date) {
                    const offset = date.getTimezoneOffset() * 60000;
                    const localISOTime = new Date(date.getTime() - offset).toISOString();
                    setCurrentNews({ ...currentNews, date: localISOTime });
                  } else {
                    setCurrentNews({ ...currentNews, date: '' });
                  }
                }}
                dateFormat="MMMM d, yyyy"
                customInput={<Input />}
                wrapperClassName="w-full"
                popperClassName="z-[100]"
                portalId="root"
              />
            </div>
            <div className="space-y-1.5 flex flex-col">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={currentNews.status || 'Draft'}
                onValueChange={(value: any) => setCurrentNews({ ...currentNews, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft (hidden from portal)</SelectItem>
                  <SelectItem value="Published">Published (visible on portal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={closeModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={!!newsToDelete}
        onClose={() => setNewsToDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete News"
        description={`Are you sure you want to delete "${newsToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
      />
    </div>
  );
}
